import type { ReactNode } from 'react';
import type { AclRoute } from './acl-routes';
import { usePermissions } from './use-permissions';
import { holdsAny } from './holds-any';

export interface CanProps {
  /**
   * The UI permission-key required to render `children`. An array means ANY of them, for a view
   * that both an "own" (`read`) and an "all" (`read-all`) holder may open.
   */
  k: string | readonly string[];
  /** The ACL route whose key set to check. */
  route: AclRoute;
  children: ReactNode;
  /** Rendered when the key is absent (default: nothing). */
  fallback?: ReactNode;
}

/**
 * Element-level permission gate. FAIL-CLOSED: renders `children` only when the
 * route's keys have resolved AND contain `k`; otherwise `fallback`.
 *
 * ⚠️ UX only — never a security boundary. Use for buttons/tabs/links; use
 * {@link withPermission} for whole-route protection.
 *
 * @example <Can k={GlobalAclKeys.sidebarClients} route={AclRoute.GLOBAL}><ClientsLink/></Can>
 */
export function Can({ k, route, children, fallback = null }: CanProps) {
  const { checkPermission } = usePermissions(route);
  return <>{holdsAny(checkPermission, k) ? children : fallback}</>;
}
